/**
 * BuildStamp
 * Tiny always-visible build/version badge so you can instantly tell which
 * environment and deploy you are looking at (LIVE vs PREVIEW vs DEV) and
 * rule out cache confusion. Reads `src/buildInfo.js` (regenerated on every
 * `yarn start` / `yarn build`) and detects the environment from hostname.
 */
import React, { useEffect, useState } from 'react';
import { BUILD_INFO } from '@/buildInfo';
import { UI_VERSION } from '@/uiVersion';

const detectEnv = () => {
  if (typeof window === 'undefined') return 'SSR';
  const host = window.location.hostname;
  if (host === 'app.keikka.work' || host === 'keikka.work' || host === 'www.keikka.work') {
    return 'LIVE';
  }
  if (host.endsWith('.preview.emergentagent.com')) return 'PREVIEW';
  if (host === 'localhost' || host === '127.0.0.1' || host.startsWith('192.168.')) {
    return 'DEV';
  }
  return 'UNKNOWN';
};

const envColor = {
  LIVE: 'bg-emerald-600',
  PREVIEW: 'bg-amber-500',
  DEV: 'bg-slate-500',
  UNKNOWN: 'bg-rose-500',
  SSR: 'bg-slate-400',
};

const BuildStamp = () => {
  const [expanded, setExpanded] = useState(false);
  const [backendHash, setBackendHash] = useState(null);

  // Fetch backend build hash once on mount so we can surface partial deploys
  // directly in the stamp without a second round-trip to /status.
  useEffect(() => {
    let cancelled = false;
    fetch(`/api/health/build?t=${Date.now()}`, {
      cache: 'no-store',
      headers: { 'cache-control': 'no-cache' },
    })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => {
        if (!cancelled && d && d.buildId) setBackendHash(d.buildId);
      })
      .catch(() => {
        /* ignore */
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Demo mode: hide the stamp if requested via `?hide_stamp=1` OR
  // `localStorage.keikka_hide_stamp === "1"`. Useful for customer-facing demos
  // where the PREVIEW/LIVE badge would be distracting. Ops can still inspect
  // the build via `document.querySelector('meta[name="build"]').content` or
  // `window.KEIKKA_BUILD`.
  const hidden = (() => {
    if (typeof window === 'undefined') return false;
    try {
      const url = new URL(window.location.href);
      if (url.searchParams.get('hide_stamp') === '1') {
        localStorage.setItem('keikka_hide_stamp', '1');
        return true;
      }
      if (url.searchParams.get('hide_stamp') === '0') {
        localStorage.removeItem('keikka_hide_stamp');
        return false;
      }
      return localStorage.getItem('keikka_hide_stamp') === '1';
    } catch (_) {
      return false;
    }
  })();

  const env = detectEnv();
  const { hash, time, deployId } = BUILD_INFO;

  // Expose for console-based debugging even when stamp is hidden.
  if (typeof window !== 'undefined') {
    window.KEIKKA_BUILD = { hash, time, deployId, env, ui: UI_VERSION, backend: backendHash };
  }

  if (hidden) return null;

  const shortTime = time && time !== '1970-01-01T00:00:00.000Z'
    ? time.replace('T', ' ').slice(0, 16) + 'Z'
    : '—';
  const color = envColor[env] || envColor.UNKNOWN;
  const mismatch = backendHash && backendHash !== hash;

  // Compact form fits mobile bottom-left:  LIVE · <fe>  (or  LIVE · <fe>≠<be> on mismatch)
  const compact = mismatch
    ? `${env} · ${hash}≠${backendHash}`
    : `${env} · ${hash}`;
  const full = `UI ${UI_VERSION} | FE: ${hash} | BE: ${backendHash || '?'} | ENV: ${env} | TIME: ${shortTime}${deployId ? ` | DEPLOY: ${deployId}` : ''}`;

  // Force-red when frontend and backend hashes disagree so it is impossible to miss.
  const bg = mismatch ? 'bg-rose-600 animate-pulse' : color;

  return (
    <button
      type="button"
      onClick={() => setExpanded((v) => !v)}
      className={`fixed bottom-1 left-1 z-[9999] ${bg} text-white text-[10px] leading-none font-mono px-2 py-1 rounded shadow-md opacity-80 hover:opacity-100 active:scale-95 transition select-text`}
      style={{ WebkitTapHighlightColor: 'transparent' }}
      aria-label="Build info"
      title={full}
      data-testid="build-stamp"
      data-env={env}
      data-hash={hash}
      data-backend-hash={backendHash || ''}
      data-ui-version={UI_VERSION}
    >
      {expanded ? full : compact}
    </button>
  );
};

export default BuildStamp;
